import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, Users, Layers, Building, BarChart3, Images, Settings, UserCog, Inbox, History, Tag, Menu, X, Newspaper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EntityPanel } from "@/components/admin/EntityPanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { StaffPanel } from "@/components/admin/StaffPanel";
import { MessagesPanel } from "@/components/admin/MessagesPanel";
import { AuditPanel } from "@/components/admin/AuditPanel";

const SCHEMAS = {
  team: {
    title: "Tim / Pengajar",
    collection: "team",
    fields: [
      { key: "name", label: "Nama", type: "text" },
      { key: "role", label: "Jabatan", type: "text" },
      { key: "desc", label: "Deskripsi Singkat", type: "textarea" },
      { key: "image_url", label: "Foto", type: "image" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ name: "", role: "", desc: "", image_url: "", order: n }),
    summary: (it) => ({ title: it.name, subtitle: it.role, image: it.image_url || "" }),
  },
  services: {
    title: "Program & Layanan",
    collection: "services",
    fields: [
      { key: "icon", label: "Ikon", type: "iconSelect" },
      { key: "title", label: "Judul", type: "text" },
      { key: "desc", label: "Deskripsi", type: "textarea" },
      { key: "points", label: "Poin-poin", type: "stringList" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ icon: "Trophy", title: "", desc: "", points: [], order: n }),
    summary: (it) => ({ title: it.title, subtitle: it.desc }),
  },
  partners: {
    title: "Mitra & Instansi",
    collection: "partners",
    fields: [
      { key: "name", label: "Nama Instansi", type: "text" },
      { key: "logo_url", label: "Logo (opsional)", type: "image" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ name: "", logo_url: "", order: n }),
    summary: (it) => ({ title: it.name, subtitle: "", image: it.logo_url || "" }),
  },
  stats: {
    title: "Statistik / Track Record",
    collection: "stats",
    fields: [
      { key: "value", label: "Angka", type: "number" },
      { key: "suffix", label: "Akhiran (mis. +)", type: "text" },
      { key: "label", label: "Keterangan", type: "text" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ value: 0, suffix: "+", label: "", order: n }),
    summary: (it) => ({ title: `${it.value}${it.suffix || ""}`, subtitle: it.label }),
  },
  albums: {
    title: "Galeri Album",
    collection: "albums",
    fields: [
      { key: "title", label: "Judul Album", type: "text" },
      { key: "subtitle", label: "Sub-judul", type: "text" },
      { key: "cover_url", label: "Foto Sampul", type: "image" },
      { key: "photos", label: "Foto dalam Album", type: "imageList" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ title: "", subtitle: "", cover_url: "", photos: [], order: n }),
    summary: (it) => ({ title: it.title, subtitle: `${(it.photos || []).length} foto`, image: it.cover_url || "" }),
  },
  categories: {
    title: "Kategori Kontak",
    collection: "categories",
    fields: [
      { key: "label", label: "Nama Kategori", type: "text" },
      { key: "desc", label: "Keterangan Singkat (tampil saat dipilih di form)", type: "textarea" },
      { key: "icon", label: "Ikon", type: "iconSelect" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ label: "", desc: "", icon: "Users", order: n }),
    summary: (it) => ({ title: it.label, subtitle: it.desc || it.icon }),
  },
  news: {
    title: "Berita & Update",
    collection: "news",
    fields: [
      { key: "title", label: "Judul Berita", type: "text" },
      { key: "date", label: "Tanggal Publikasi", type: "text" },
      { key: "desc", label: "Isi Berita", type: "textarea" },
      { key: "image_url", label: "Foto Sampul", type: "image" },
      { key: "order", label: "Urutan", type: "number" },
    ],
    makeEmpty: (n) => ({ title: "", date: "", desc: "", image_url: "", order: n }),
    summary: (it) => ({ title: it.title, subtitle: it.date, image: it.image_url || "" }),
  },
};

const TABS = [
  { id: "settings", label: "Konten & Kontak", icon: Settings },
  { id: "team", label: "Tim", icon: Users },
  { id: "services", label: "Program", icon: Layers },
  { id: "partners", label: "Mitra", icon: Building },
  { id: "stats", label: "Statistik", icon: BarChart3 },
  { id: "albums", label: "Galeri", icon: Images },
  { id: "contacts", label: "Pesan Masuk", icon: Inbox },
  { id: "categories", label: "Kategori Kontak", icon: Tag },
  { id: "audit", label: "Riwayat", icon: History },
  { id: "staff", label: "Akun Staf", icon: UserCog, adminOnly: true },
  { id: "news", label: "Berita", icon: Newspaper },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("settings");
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const tabs = TABS.filter((t) => (t.adminOnly ? isAdmin : true));

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const NavItems = () => (
    <nav className="space-y-1">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setMobileOpen(false); }}
            data-testid={`tab-${t.id}`}
            className={`w-full flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition ${
              tab === t.id ? "bg-cyan/10 text-cyan border border-cyan/30" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
            }`}
          >
            <Icon className="w-4 h-4" /> {t.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-navy text-white font-body">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="flex items-center justify-between px-4 md:px-6 py-3">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1.5 text-white" onClick={() => setMobileOpen((v) => !v)} data-testid="admin-mobile-toggle">
              {mobileOpen ? <X /> : <Menu />}
            </button>
            <span className="flex items-center gap-2.5">
              <img src="/logo.webp" alt="Binara Labs" className="w-8 h-8 object-contain" />
              <span className="font-heading font-extrabold tracking-tight">BINARA<span className="text-gold">.</span>LABS <span className="text-slate-500 font-body font-normal text-sm">Admin</span></span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-cyan transition" data-testid="view-site-link">
              <ExternalLink className="w-4 h-4" /> Lihat Situs
            </Link>
            <span className="hidden md:block text-sm text-slate-400">{user?.name}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-200 hover:text-red-400 hover:border-red-400/40 transition">
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-white/10 p-4 min-h-[calc(100vh-57px)]">
          <NavItems />
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-navy/95 backdrop-blur-md pt-20 px-4" onClick={() => setMobileOpen(false)}>
            <div onClick={(e) => e.stopPropagation()}><NavItems /></div>
          </div>
        )}

        <main className="flex-1 p-5 md:p-8 max-w-6xl w-full">
          {tab === "settings" && <SettingsPanel />}
          {tab === "contacts" && <MessagesPanel />}
          {tab === "audit" && <AuditPanel />}
          {tab === "staff" && <StaffPanel />}
          {SCHEMAS[tab] && <EntityPanel {...SCHEMAS[tab]} />}
        </main>
      </div>
    </div>
  );
}
